/**
 * The signed-in tree. `requireAuth` sends a visitor with no session to
 * `/login`, keeping the address to come back to.
 *
 * Also where a lost company lands (see shell/companyAccess): the permissions
 * are read again; only when they no longer list the company is the person
 * told (once per company) and the app opens on a company they still hold.
 */
import { useEffect, useRef, type JSX } from "react";
import { Outlet, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "@linkhr/auth";
import { useAuth } from "@linkhr/auth/react";
import { useToast } from "@linkhr/design-system";
import { useTranslation } from "@linkhr/i18n";
import { mePermissionsKey, type MePermissionsView } from "../api/useMePermissions";
import { onCompanyAccessLost } from "../shell/companyAccess";
import { gateFor } from "../shell/gate";

export const Route = createFileRoute("/_auth")({
  beforeLoad: requireAuth,
  component: SignedInLayout,
});

function SignedInLayout(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation("accounting");
  const { tenantId } = useAuth();
  // Polling keeps failing the same way; say it once per company.
  const told = useRef(new Set<string>());

  useEffect(
    () =>
      onCompanyAccessLost((companyId) => {
        const key = mePermissionsKey(tenantId);
        void queryClient
          // Several refusals at once share one re-read.
          .invalidateQueries({ queryKey: key }, { cancelRefetch: false })
          .then(() => {
            const fresh = queryClient.getQueryData<MePermissionsView>(key);
            // The same 403 means "company lost" and "this grant lacks the route's code": only the
            // first goes home. The refused screen shows its own message; a product that is off
            // renders its own page from the same read.
            if (fresh !== undefined && gateFor(fresh, companyId).kind !== "no_company") return;
            if (!told.current.has(companyId)) {
              told.current.add(companyId);
              toast({ variant: "warning", description: t("accounting:app.company_revoked") });
            }
            return navigate({ to: "/", replace: true });
          });
      }),
    [queryClient, navigate, toast, t, tenantId],
  );

  return <Outlet />;
}
